import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../apiBase";
import { ownerSecretHeader, setOwnerSecret } from "../ownerSecret";

function CreateAirplanePage() {
  const navigate = useNavigate();

  // -----------------------------
  // top-level aircraft fields
  // -----------------------------
  const [tailNumber, setTailNumber] = useState("");
  const [makeModel, setMakeModel] = useState("");
  const [engine, setEngine] = useState("");
  const [imageFile, setImageFile] = useState(null);

  // -----------------------------
  // vspeeds
  // -----------------------------
  const [vspeeds, setVspeeds] = useState([
    { id: "BestGlide", config: "", vs1: "", vs2: "", units: "Kts" },
    { id: "Vy", config: "", vs1: "", vs2: "", units: "Kts" },
    { id: "Vx", config: "", vs1: "", vs2: "", units: "Kts" },
    { id: "VA", config: "", vs1: "", vs2: "", units: "Kts" },
    { id: "Vno", config: "", vs1: "", vs2: "", units: "Kts" },
    { id: "Vne", config: "", vs1: "", vs2: "", units: "Kts" },
    { id: "VLO", config: "", vs1: "", vs2: "", units: "Kts" },
    { id: "Vs1", config: "", vs1: "", vs2: "", units: "Kts" },
    { id: "Vrotate", config: "", vs1: "", vs2: "", units: "Kts" },
    { id: "Vldg", config: "", vs1: "", vs2: "", units: "Kts" }
  ]);

  // -----------------------------
  // weight & balance
  // -----------------------------
  const [weightBalance, setWeightBalance] = useState([
    { id: "Basic Empty Weight", Weight: "", Arm: "" },
    { id: "Pilot", Weight: "", Arm: "" },
    { id: "Copilot", Weight: "", Arm: "" },
    { id: "RearPass1", Weight: "", Arm: "" },
    { id: "RearPass2", Weight: "", Arm: "" },
    { id: "Baggage", Weight: "", Arm: "" },
  ]);

  // -----------------------------
  // Fuel
  // -----------------------------
  const [fuel, setFuel] = useState({
    id: "fuel",
    initialFuelGal: "",
    runupFuelGal: "2.04",
    usableGals: "",
    unusableGals: "4.0",
    lbsPerGal: "6.1",
    Arm: "",
    type: "100LL",
    units: "Gallons"
  });

  // -----------------------------
  // Limits
  // -----------------------------
  const [limits, setLimits] = useState({
    id: "limits",
    maxGrossLbs: "",
    cgAftIn: "",
    cgForwIn: "",
    forwardEnvelope: [
      { cgIn: "", weightLbs: "" },
      { cgIn: "", weightLbs: "" },
      { cgIn: "", weightLbs: "" },
      { cgIn: "", weightLbs: "" },
      { cgIn: "", weightLbs: "" },
      { cgIn: "", weightLbs: "" }
    ]
  });

  // -----------------------------
  // rollout
  // -----------------------------
  const [rollDistance, setRollDistance] = useState([
    {
      id: "Takeoff_15C",
      temperature: "15°C",
      Sea_Level: "",
      Field_Elevation: "",
      units: "Feet"
    },
    {
      id: "Landing_15C",
      temperature: "15°C",
      Sea_Level: "",
      Field_Elevation: "",
      units: "Feet"
    }
  ]);

  const [serverMessage, setServerMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // -----------------------------
  // generic row updater
  // -----------------------------
  const updateRow = (rows, setRows, index, field, value) => {
    const copy = [...rows];
    copy[index] = { ...copy[index], [field]: value };
    setRows(copy);
  };

  const addVspeedRow = () => {
    setVspeeds([...vspeeds, { id: "", config: "", vs1: "", vs2: "", units: "Kts" }]);
  };

  const addWeightBalanceRow = () => {
    setWeightBalance([...weightBalance, { id: "", Weight: "", Arm: "" }]);
  };

  const addRollDistanceRow = () => {
    setRollDistance([
      ...rollDistance,
      { id: "", temperature: "", Sea_Level: "", Field_Elevation: "", units: "Feet" }
    ]);
  };

  const deleteRow = (rows, setRows, index) => {
    const copy = [...rows];
    copy.splice(index, 1);
    setRows(copy);
  };

  const removeBlankRows = (rows) => {
    return rows.filter((row) => {
      const values = Object.values(row || {});
      return values.some((v) => String(v ?? "").trim() !== "");
    });
  };

  // -----------------------------
  // upload airplane picture
  // -----------------------------
  const uploadPictureForAirplane = async (airplaneId, tail) => {
    if (!imageFile) {
      return;
    }

    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("airplaneId", airplaneId);

    const response = await fetch(`${API_BASE}/api/upload-picture`, {
      method: "POST",
      headers: ownerSecretHeader(tail),
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Airplane created but image upload failed.");
    }
  };

  // -----------------------------
  // save airplane
  // -----------------------------
  const saveAirplane = async () => {
    setServerMessage("");

    const cleanTail = tailNumber.trim().toUpperCase();

    if (!cleanTail) {
      setServerMessage("Tail Number is required.");
      return;
    }

    const payload = {
      tailNumber: cleanTail,
      makeModel: makeModel.trim(),
      engine: engine.trim(),
      imageUrl: "",
      thumbUrl: "",
      vspeeds: removeBlankRows(vspeeds),
      weightBalance: removeBlankRows([...weightBalance, fuel, limits]),
      roll_distance: removeBlankRows(rollDistance),
    };

    try {
      setIsSaving(true);

      const response = await fetch(`${API_BASE}/api/aircraft`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...ownerSecretHeader(cleanTail),
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      setServerMessage(data.message || JSON.stringify(data));

      if (response.ok) {
        // Server returns a freshly-minted owner secret only when this
        // aircraft didn't exist before — save it so future edits work.
        if (data.ownerSecret) {
          setOwnerSecret(cleanTail, data.ownerSecret);
        }

        await uploadPictureForAirplane(cleanTail, cleanTail);

        navigate("/");
      } else if (response.status === 403) {
        setServerMessage("You're not authorized to modify an aircraft with this tail number.");
      }

    } catch (err) {
      setServerMessage(`Save failed: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <article className="App-article">
      <h2>Create Airplane</h2>

      <section style={{ marginBottom: "24px" }}>
        <table id="create-aircraft-info">
          <caption className="caption_text">Aircraft Information</caption>
          <thead>
            <tr>
              <th>Tail Number</th>
              <th>Make / Model</th>
              <th>Engine</th>
              <th>Picture</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <input value={tailNumber} onChange={(e) => setTailNumber(e.target.value)} placeholder="N12345" />
              </td>
              <td>
                <input value={makeModel} onChange={(e) => setMakeModel(e.target.value)} placeholder="Cessna 172S" />
              </td>
              <td>
                <input value={engine} onChange={(e) => setEngine(e.target.value)} placeholder="Lycoming O-360-A4M" />
              </td>
              <td>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
                  <label style={{ padding: "6px 10px", background: "#f0f0f0", border: "1px solid #666", borderRadius: "4px", cursor: "pointer", fontSize: "13px" }}>
                    Select Picture
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: "none" }}
                      onChange={(e) => setImageFile(e.target.files[0])}
                    />
                  </label>
                  <div style={{ fontSize: "12px", textAlign: "center" }}>
                    {imageFile ? imageFile.name : "No image selected"}
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <table id="create-vspeed-table">
          <caption className="caption_text">Vspeeds</caption>
          <thead>
            <tr>
              <th>ID</th>
              <th>Config</th>
              <th>Vsp1</th>
              <th>Vsp2</th>
              <th>Units</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {vspeeds.map((row, index) => (
              <tr key={index}>
                <td><input value={row.id} onChange={(e) => updateRow(vspeeds, setVspeeds, index, "id", e.target.value)} /></td>
                <td><input value={row.config} onChange={(e) => updateRow(vspeeds, setVspeeds, index, "config", e.target.value)} /></td>
                <td><input value={row.vs1} onChange={(e) => updateRow(vspeeds, setVspeeds, index, "vs1", e.target.value)} /></td>
                <td><input value={row.vs2} onChange={(e) => updateRow(vspeeds, setVspeeds, index, "vs2", e.target.value)} /></td>
                <td><input value={row.units} onChange={(e) => updateRow(vspeeds, setVspeeds, index, "units", e.target.value)} /></td>
                <td><button type="button" onClick={() => deleteRow(vspeeds, setVspeeds, index)}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" onClick={addVspeedRow}>Add Vspeed Row</button>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <table id="create-wtbal-table">
          <caption className="caption_text">Weight &amp; Balance Stations</caption>
          <thead style={{ textAlign: "center" }}>
            <tr>
              <th>ID</th>
              <th>Weight</th>
              <th>Arm</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {weightBalance.map((row, index) => (
              <tr key={index}>
                <td><input value={row.id} onChange={(e) => updateRow(weightBalance, setWeightBalance, index, "id", e.target.value)} placeholder="Pilot" /></td>
                <td><input value={row.Weight || ""} onChange={(e) => updateRow(weightBalance, setWeightBalance, index, "Weight", e.target.value)} placeholder="200" /></td>
                <td><input value={row.Arm || ""} onChange={(e) => updateRow(weightBalance, setWeightBalance, index, "Arm", e.target.value)} placeholder="40.25" /></td>
                <td><button type="button" onClick={() => deleteRow(weightBalance, setWeightBalance, index)}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" onClick={addWeightBalanceRow}>Add Weight &amp; Balance Row</button>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <table id="create-fuel-table">
          <caption className="caption_text">Fuel</caption>
          <thead>
            <tr>
              <th>Initial Fuel Gal</th>
              <th>Runup Fuel Gal</th>
              <th>Usable Gals</th>
              <th>Unusable Gals</th>
              <th>Lbs/Gal</th>
              <th>Arm</th>
              <th>Type</th>
              <th>Units</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><input value={fuel.initialFuelGal} onChange={(e) => setFuel({ ...fuel, initialFuelGal: e.target.value })} placeholder="52" /></td>
              <td><input value={fuel.runupFuelGal} onChange={(e) => setFuel({ ...fuel, runupFuelGal: e.target.value })} placeholder="2.1" /></td>
              <td><input value={fuel.usableGals} onChange={(e) => setFuel({ ...fuel, usableGals: e.target.value })} placeholder="52" /></td>
              <td><input value={fuel.unusableGals} onChange={(e) => setFuel({ ...fuel, unusableGals: e.target.value })} placeholder="4" /></td>
              <td>
                <input
                  type="number"
                  step="any"
                  value={fuel.lbsPerGal}
                  onChange={(e) =>
                    setFuel({
                      ...fuel,
                      lbsPerGal: e.target.value
                    })
                  }
                  placeholder="6.1"
                />
              </td>

              <td><input value={fuel.Arm} onChange={(e) => setFuel({ ...fuel, Arm: e.target.value })} placeholder="48.43" /></td>
              <td><input value={fuel.type} onChange={(e) => setFuel({ ...fuel, type: e.target.value })} placeholder="100LL" /></td>
              <td><input value={fuel.units} onChange={(e) => setFuel({ ...fuel, units: e.target.value })} placeholder="Gallons" /></td>
            </tr>
          </tbody>
        </table>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <table id="create-limits-table">
          <caption className="caption_text">Limits</caption>
          <thead>
            <tr style={{ textAlign: "center" }}>
              <th>Max Gross Lbs</th>
              <th>CG Aft In</th>
              <th>CG Forward In</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><input value={limits.maxGrossLbs} onChange={(e) => setLimits({ ...limits, maxGrossLbs: e.target.value })} placeholder="2591" /></td>
              <td><input value={limits.cgAftIn} onChange={(e) => setLimits({ ...limits, cgAftIn: e.target.value })} placeholder="47" /></td>
              <td><input value={limits.cgForwIn} onChange={(e) => setLimits({ ...limits, cgForwIn: e.target.value })} placeholder="35" /></td>
            </tr>
          </tbody>
        </table>
        <table id="create-forward-envelope-table">
          <caption className="caption_text">Forward Envelope</caption>
          <thead>
            <tr>
              <th>Point</th>
              <th>CG In</th>
              <th>Weight Lbs</th>
            </tr>
          </thead>
          <tbody>
            {limits.forwardEnvelope.map((point, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>
                  <input
                    value={point.cgIn}
                    onChange={(e) => {
                      const copy = [...limits.forwardEnvelope];
                      copy[index] = { ...copy[index], cgIn: e.target.value };
                      setLimits({ ...limits, forwardEnvelope: copy });
                    }}
                    placeholder="42"
                  />
                </td>
                <td>
                  <input
                    value={point.weightLbs}
                    onChange={(e) => {
                      const copy = [...limits.forwardEnvelope];
                      copy[index] = { ...copy[index], weightLbs: e.target.value };
                      setLimits({ ...limits, forwardEnvelope: copy });
                    }}
                    placeholder="1501"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <table id="create-rollout-table">
          <caption className="caption_text">Rollout</caption>
          <thead>
            <tr>
              <th>ID</th>
              <th>Temperature</th>
              <th>Sea Level</th>
              <th>Field Elevation</th>
              <th>Units</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {rollDistance.map((row, index) => (
              <tr key={index}>
                <td><input value={row.id} onChange={(e) => updateRow(rollDistance, setRollDistance, index, "id", e.target.value)} placeholder="Takeoff_15C" /></td>
                <td><input value={row.temperature} onChange={(e) => updateRow(rollDistance, setRollDistance, index, "temperature", e.target.value)} placeholder="15°C" /></td>
                <td><input value={row.Sea_Level} onChange={(e) => updateRow(rollDistance, setRollDistance, index, "Sea_Level", e.target.value)} placeholder="1600" /></td>
                <td><input value={row.Field_Elevation} onChange={(e) => updateRow(rollDistance, setRollDistance, index, "Field_Elevation", e.target.value)} placeholder="1951" /></td>
                <td><input value={row.units} onChange={(e) => updateRow(rollDistance, setRollDistance, index, "units", e.target.value)} placeholder="Feet" /></td>
                <td><button type="button" onClick={() => deleteRow(rollDistance, setRollDistance, index)}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" onClick={addRollDistanceRow}>Add Rollout Row</button>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <button type="button" onClick={saveAirplane} disabled={isSaving} style={{ marginRight: "10px" }}>
          {isSaving ? "Saving..." : "Save Airplane"}
        </button>
        <button type="button" onClick={() => navigate("/")} disabled={isSaving}>
          Cancel
        </button>
      </section>

      {serverMessage && (
        <div style={{ marginTop: "12px" }}>
          {serverMessage}
        </div>
      )}
    </article>
  );
}

export default CreateAirplanePage;